import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

// ── Types (mirror backend/src/modules/referrals) ────────────────────────────

export type Vertical = 'dental' | 'medical';
export type ReferralStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'scheduled'
  | 'completed'
  | 'declined'
  | 'cancelled';
export type Urgency = 'routine' | 'soon' | 'urgent';
export type BodySide = 'left' | 'right' | 'bilateral' | 'midline';
export type DeliveryMethod = 'email' | 'text' | 'print';

export interface DeliveryOptions {
  email: { available: boolean; to: string | null };
  inbox: { available: boolean };
  /** Only offered when the sender's texting number is live AND the office has a number known to receive texts. */
  text: {
    available: boolean;
    to: string | null;
    /** mobile | voip | given (office-supplied) when available. */
    lineType: string | null;
    /** The sender's masked texting number. */
    from: string | null;
    reason: string | null;
  };
  print: { available: true };
}

export interface ToothDef {
  code: string;
  fdi: string;
  arch: 'upper' | 'lower';
  quadrant: 'UR' | 'UL' | 'LL' | 'LR';
  position: number;
  primary: boolean;
  type: 'incisor' | 'canine' | 'premolar' | 'molar';
  name: string;
}

export interface BodyRegionDef {
  key: string;
  label: string;
  group: string;
  sided: boolean;
}

export interface BodyRegionSelection {
  region: string;
  side?: BodySide | null;
}

export interface ReferralSettings {
  locationId: string;
  customReasons: string[];
  notifyEmails: string[];
  acceptingNewPatients: boolean;
  intakeInstructions: string | null;
  toothNumbering: 'universal' | 'fdi';
  reminderAfterHours: number;
  /** Days after sending with no confirmation from anyone before the slip is flagged; 0 = never. */
  unconfirmedAfterDays: number;
  /** Default for new slips: text the patient a check-in a week later (needs a live texting number). */
  patientFollowUp: boolean;
}

export type RecipientStatus =
  | 'pending'
  | 'sent'
  | 'viewed'
  | 'scheduled'
  | 'completed'
  | 'declined'
  | 'superseded'
  | 'cancelled';
export type ReferralOutcome = 'scheduled' | 'seen' | 'not_seen' | 'declined_all';
export type OutcomeSource = 'recipient' | 'report' | 'front_desk' | 'patient_text' | 'system';
export const MAX_RECIPIENTS = 5;

export interface ReferralTemplate {
  vertical: Vertical;
  specialty: string;
  specialtyLabel: string;
  isGeneralist: boolean;
  lexicon: { providerNoun: string; patientNoun: string; practiceNoun: string };
  specialties: Array<{ value: string; label: string }>;
  reasons: string[];
  teeth: ToothDef[];
  bodyRegions: BodyRegionDef[];
  urgencies: Urgency[];
  providers: Array<{ id: string; name: string }>;
  settings: ReferralSettings;
  patientText: { available: boolean; reason: string | null };
  officeText: { available: boolean; reason: string | null };
  attachmentsAvailable: boolean;
  aiAvailable: boolean;
}

export interface DirectoryEntry {
  id: string | null;
  name: string;
  specialty: string | null;
  specialtyLabel: string | null;
  practiceType: 'generalist' | 'specialist';
  phone: string | null;
  smsPhone: string | null;
  phoneLineType: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  website: string | null;
  googlePlaceId: string | null;
  googleRating: number | null;
  googleReviewCount: number | null;
  latitude: number | null;
  longitude: number | null;
  distanceMiles: number | null;
  onPracticeNova: boolean;
  acceptingNewPatients: boolean | null;
  intakeInstructions: string | null;
  providers: Array<{ id: string; name: string; title: string | null }>;
  priorReferrals: number;
  source: 'directory' | 'practicenova' | 'places';
  why: string[];
}

export interface SuggestedResult {
  /** Offices this location has referred to before, most often first. */
  frequent: DirectoryEntry[];
  specialty: string | null;
  specialtyLabel: string | null;
  /** selected = the sender picked it; inferred = reasons/comment implied it; none = no signal yet. */
  basis: 'selected' | 'inferred' | 'none';
  /** Nearby offices of that specialty; empty when basis is none. */
  entries: DirectoryEntry[];
}

export interface ReferralTriage {
  urgency: Urgency;
  flags: string[];
  summary: string;
  nextStep: string;
  specialtyHint: string | null;
  rationale: string;
}

export interface ReferralAttachment {
  id: string;
  side: 'sender' | 'recipient';
  kind: string;
  mimeType: string;
  sizeBytes: number;
  originalFilename: string;
  status: string;
  label: string | null;
  createdAt: string;
  url: string | null;
}

export interface ReferralReport {
  id: string;
  authorName: string;
  body: string;
  procedures: string[];
  outcome: string | null;
  followUp: string | null;
  createdAt: string;
}

export interface ReferralEvent {
  id: string;
  type: string;
  actor: string;
  actorName: string | null;
  note: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export interface ReferralRecipient {
  id: string;
  name: string;
  specialty: string | null;
  specialtyLabel: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  website: string | null;
  onPracticeNova: boolean;
}

export interface ReferralPatientDetails {
  insurance?: string;
  memberId?: string;
  preferredLanguage?: string;
  address?: string;
  notes?: string;
}

/** One office a referral was offered to. */
export interface RecipientOption {
  id: string;
  position: number;
  status: RecipientStatus;
  practice: ReferralRecipient;
  linkedLocationId: string | null;
  preferredProviderId: string | null;
  preferredProviderName: string | null;
  deliveryMethods: string[];
  emailSentAt: string | null;
  emailError: string | null;
  textSentAt: string | null;
  textError: string | null;
  viewedAt: string | null;
  scheduledAt: string | null;
  scheduledFor: string | null;
  completedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  reminderCount: number;
  isChosen: boolean;
  linkPreview: string | null;
  delivery: DeliveryOptions | null;
}

export interface ReferralListItem {
  id: string;
  status: ReferralStatus;
  direction: 'sent' | 'received';
  patientName: string;
  patientLast4: string | null;
  otherPartyName: string;
  recipientCount: number;
  outcome: ReferralOutcome | null;
  /** The window passed and nobody confirmed anything. */
  isUnconfirmed: boolean;
  referringDoctorName: string | null;
  reasons: string[];
  urgency: Urgency;
  aiUrgency: Urgency | null;
  needsAction: boolean;
  attachmentCount: number;
  reportCount: number;
  sentAt: string | null;
  updatedAt: string;
  createdAt: string;
}

export interface ReferralDetail extends ReferralListItem {
  practiceId: string;
  locationId: string;
  vertical: Vertical;
  senderPracticeName: string;
  senderLocationName: string;
  senderPhone: string | null;
  /** The chosen office, or the only one; null while a multi-office slip is still open. */
  recipient: ReferralRecipient | null;
  recipientLocationId: string | null;
  recipients: RecipientOption[];
  chosenRecipientId: string | null;
  outcomeSource: OutcomeSource | null;
  outcomeAt: string | null;
  outcomeNote: string | null;
  unconfirmedAt: string | null;
  patientFollowUp: boolean;
  patientReplyKind: string | null;
  patientReplyAt: string | null;
  /** On the receiving side: which option this office is. */
  viewerRecipientId: string | null;
  preferredProviderId: string | null;
  preferredProviderName: string | null;
  referringStaffMemberId: string | null;
  patientFirstName: string;
  patientLastName: string;
  patientPhone: string | null;
  patientPhoneMasked: string | null;
  patientEmail: string | null;
  patientDob: string | null;
  patientDetails: ReferralPatientDetails | null;
  textPatientCopy: boolean;
  teeth: string[];
  teethLabel: string;
  bodyRegions: BodyRegionSelection[];
  bodyRegionsLabel: string;
  comment: string | null;
  aiSummary: string | null;
  aiTriage: ReferralTriage | null;
  viewedAt: string | null;
  scheduledAt: string | null;
  scheduledFor: string | null;
  completedAt: string | null;
  declinedAt: string | null;
  cancelledAt: string | null;
  declineReason: string | null;
  deliveryMethods: string[];
  patientTextSentAt: string | null;
  patientTextError: string | null;
  recipientEmailSentAt: string | null;
  recipientEmailError: string | null;
  recipientTextSentAt: string | null;
  recipientTextError: string | null;
  reminderCount: number;
  attachments: ReferralAttachment[];
  reports: ReferralReport[];
  events: ReferralEvent[];
  warnings: string[];
  recipientLinkPreview: string | null;
  patientLinkPreview: string | null;
  delivery: DeliveryOptions | null;
}

export interface ReferralStats {
  sentTotal: number;
  sent30d: number;
  received30d: number;
  awaitingAction: number;
  awaitingResponse: number;
  scheduledRate30d: number | null;
  medianHoursToView: number | null;
  topRecipients: Array<{ name: string; count: number }>;
}

export interface ReferralDraftInput {
  locationId: string;
  patientFollowUp?: boolean;
  referringDoctorName?: string | null;
  referringStaffMemberId?: string | null;
  patientFirstName?: string;
  patientLastName?: string;
  patientPhone?: string | null;
  patientEmail?: string | null;
  patientDob?: string | null;
  patientDetails?: ReferralPatientDetails | null;
  textPatientCopy?: boolean;
  reasons?: string[];
  teeth?: string[];
  bodyRegions?: BodyRegionSelection[] | null;
  comment?: string | null;
  urgency?: Urgency;
}

export interface DirectoryPracticeInput {
  locationId: string;
  name: string;
  phone?: string | null;
  smsPhone?: string | null;
  email?: string | null;
  fax?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  specialty?: string | null;
  practiceType?: 'generalist' | 'specialist';
  googlePlaceId?: string | null;
  providers?: string[];
}

export interface StatusChangeInput {
  status: 'viewed' | 'scheduled' | 'completed' | 'declined';
  scheduledFor?: string | null;
  note?: string | null;
  actorName?: string;
  /** Sender side: which office, when the slip offered several. */
  recipientId?: string | null;
}

export interface OutcomeInput {
  outcome: 'seen' | 'scheduled' | 'not_seen';
  recipientId?: string | null;
  note?: string | null;
}

export interface SendInput {
  methods?: DeliveryMethod[];
  recipients?: Array<{ recipientId: string; methods: DeliveryMethod[] }>;
}

export interface ReportInput {
  authorName: string;
  body: string;
  procedures: string[];
  outcome?: string | null;
  followUp?: string | null;
}

export interface AttachmentUploadInput {
  filename: string;
  contentType: string;
  sizeBytes: number;
  kind?: 'xray' | 'photo' | 'document' | 'report' | 'other';
}

export interface AttachmentUploadTicket {
  attachment: ReferralAttachment;
  uploadUrl: string;
  expiresInSeconds: number;
}

export interface PublicReferralView {
  referral: Omit<
    ReferralDetail,
    | 'warnings'
    | 'recipientLinkPreview'
    | 'patientLinkPreview'
    | 'delivery'
    | 'patientTextError'
    | 'recipientEmailError'
    | 'recipientTextError'
  >;
  recipientOnPracticeNova: boolean;
  /** The patient booked with another office on the same slip; this copy is read-only. */
  superseded: boolean;
}

export interface ReferralGateInfo {
  referralId: string;
  officeName: string;
  senderPracticeName: string;
  emailHint: string | null;
  emailOnFile: boolean;
  onPracticeNova: boolean;
  status: string;
}

export interface ClaimedOffice {
  id: string;
  name: string;
  specialty: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  claimedAt: string | null;
  lastSeenAt: string | null;
  contacts: Array<{ email: string; contactName: string | null; verifiedAt: string; leadDetailsAt: string | null }>;
  referralsReceived: number;
  referralsScheduled: number;
  onPracticeNova: boolean;
}

export interface PatientOffice {
  position: number;
  name: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  website: string | null;
  mapsUrl: string | null;
  chosen: boolean;
  declined: boolean;
}

export interface PatientReferralView {
  patientFirstName: string;
  fromPractice: { name: string; phone: string | null };
  toPractice: Omit<PatientOffice, 'position' | 'chosen' | 'declined'>;
  options: PatientOffice[];
  reasons: string[];
  siteLabel: string | null;
  referringDoctorName: string | null;
  status: ReferralStatus;
  scheduledFor: string | null;
  sentAt: string | null;
}

// ── Client ──────────────────────────────────────────────────────────────────

const q = (locationId: string) => ({ params: { locationId } });

const VIEWER_KEY = 'pn_referral_viewer';

/** The viewer token this browser earned at the referral gate, if any. */
export function getReferralViewerToken(): string | null {
  try {
    return localStorage.getItem(VIEWER_KEY);
  } catch {
    return null;
  }
}

export function setReferralViewerToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(VIEWER_KEY, token);
    else localStorage.removeItem(VIEWER_KEY);
  } catch {
    /* storage unavailable: the gate simply asks again next time */
  }
}

function viewer() {
  const token = getReferralViewerToken();
  return token ? { headers: { 'X-Referral-Viewer': token } } : {};
}

async function unwrap<T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  return (await p).data.data;
}

export const referralsApi = {
  getTemplate: (locationId: string, recipientPracticeId: string | null) =>
    unwrap<ReferralTemplate>(
      apiClient.get('/referrals/template', {
        params: { locationId, ...(recipientPracticeId ? { recipientPracticeId } : {}) },
      }),
    ),
  getSettings: (locationId: string) =>
    unwrap<ReferralSettings>(apiClient.get('/referrals/settings', q(locationId))),
  updateSettings: (input: Partial<ReferralSettings> & { locationId: string }) =>
    unwrap<ReferralSettings>(apiClient.put('/referrals/settings', input)),
  getStats: (locationId: string) =>
    unwrap<ReferralStats>(apiClient.get('/referrals/stats', q(locationId))),

  searchDirectory: (locationId: string, search: string) =>
    unwrap<DirectoryEntry[]>(
      apiClient.get('/referrals/directory/search', { params: { locationId, q: search } }),
    ),
  suggested: (
    locationId: string,
    input: { specialty?: string; reasons?: string[]; comment?: string },
  ) =>
    unwrap<SuggestedResult>(
      apiClient.get('/referrals/directory/suggested', {
        params: {
          locationId,
          ...(input.specialty ? { specialty: input.specialty } : {}),
          ...(input.reasons?.length ? { reasons: input.reasons.join('|') } : {}),
          ...(input.comment ? { comment: input.comment } : {}),
        },
      }),
    ),
  getDirectoryPractice: (locationId: string, id: string) =>
    unwrap<DirectoryEntry>(apiClient.get(`/referrals/directory/${id}`, q(locationId))),
  getDeliveryOptions: (locationId: string, id: string) =>
    unwrap<DeliveryOptions>(apiClient.get(`/referrals/directory/${id}/delivery`, q(locationId))),
  createDirectoryPractice: (input: DirectoryPracticeInput) =>
    unwrap<DirectoryEntry>(apiClient.post('/referrals/directory', input)),
  updateDirectoryPractice: (id: string, input: Partial<DirectoryPracticeInput> & { locationId: string }) =>
    unwrap<DirectoryEntry>(apiClient.patch(`/referrals/directory/${id}`, input)),
  addProvider: (id: string, input: { locationId: string; name: string; title?: string | null }) =>
    unwrap<DirectoryEntry>(apiClient.post(`/referrals/directory/${id}/providers`, input)),

  list: (locationId: string, filter: { box: string; status?: string; q?: string }) =>
    unwrap<ReferralListItem[]>(
      apiClient.get('/referrals', {
        params: {
          locationId,
          box: filter.box,
          ...(filter.status && filter.status !== 'all' ? { status: filter.status } : {}),
          ...(filter.q ? { q: filter.q } : {}),
        },
      }),
    ),
  create: (input: ReferralDraftInput) => unwrap<ReferralDetail>(apiClient.post('/referrals', input)),
  get: (locationId: string, id: string) =>
    unwrap<ReferralDetail>(apiClient.get(`/referrals/${id}`, q(locationId))),
  update: (id: string, input: ReferralDraftInput) =>
    unwrap<ReferralDetail>(apiClient.patch(`/referrals/${id}`, input)),
  deleteDraft: (locationId: string, id: string) =>
    unwrap<{ deleted: boolean }>(apiClient.delete(`/referrals/${id}`, q(locationId))),
  send: (locationId: string, id: string, input: SendInput = {}) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/send`, input, q(locationId))),
  addRecipient: (
    locationId: string,
    id: string,
    input: { referralPracticeId: string; preferredProviderId?: string | null; preferredProviderName?: string | null },
  ) => unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/recipients`, { locationId, ...input })),
  updateRecipient: (
    locationId: string,
    id: string,
    recipientId: string,
    input: { preferredProviderId?: string | null; preferredProviderName?: string | null },
  ) => unwrap<ReferralDetail>(apiClient.patch(`/referrals/${id}/recipients/${recipientId}`, { locationId, ...input })),
  removeRecipient: (locationId: string, id: string, recipientId: string) =>
    unwrap<ReferralDetail>(apiClient.delete(`/referrals/${id}/recipients/${recipientId}`, q(locationId))),
  recordOutcome: (locationId: string, id: string, input: OutcomeInput) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/outcome`, input, q(locationId))),
  cancel: (locationId: string, id: string, reason?: string) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/cancel`, { reason }, q(locationId))),
  retryPatientText: (locationId: string, id: string) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/patient-text`, {}, q(locationId))),
  remind: (locationId: string, id: string, recipientId?: string | null) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/remind`, recipientId ? { recipientId } : {}, q(locationId))),
  changeStatus: (locationId: string, id: string, input: StatusChangeInput) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/status`, input, q(locationId))),
  addNote: (locationId: string, id: string, note: string) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/notes`, { note }, q(locationId))),
  addReport: (locationId: string, id: string, input: ReportInput) =>
    unwrap<ReferralDetail>(apiClient.post(`/referrals/${id}/reports`, input, q(locationId))),
  requestUpload: (locationId: string, id: string, input: AttachmentUploadInput) =>
    unwrap<AttachmentUploadTicket>(
      apiClient.post(`/referrals/${id}/attachments/upload-url`, input, q(locationId)),
    ),
  confirmUpload: (locationId: string, id: string, attachmentId: string) =>
    unwrap<ReferralAttachment>(
      apiClient.post(`/referrals/${id}/attachments/${attachmentId}/confirm`, {}, q(locationId)),
    ),
  deleteAttachment: (locationId: string, id: string, attachmentId: string) =>
    unwrap<{ deleted: boolean }>(
      apiClient.delete(`/referrals/${id}/attachments/${attachmentId}`, q(locationId)),
    ),
  draftComment: (input: {
    locationId: string;
    recipientPracticeId?: string | null;
    reasons: string[];
    teeth: string[];
    bodyRegions: BodyRegionSelection[];
    notes: string;
    urgency?: Urgency;
  }) => unwrap<{ comment: string | null }>(apiClient.post('/referrals/ai/draft-comment', input)),

  // Public (token). Past the gate every call carries the viewer token this browser earned by
  // entering the one-time code (X-Referral-Viewer); without it the API answers 401 CODE_REQUIRED.
  publicGate: (token: string) => unwrap<ReferralGateInfo>(apiClient.get(`/refer/v/${token}/gate`)),
  publicRequestCode: (token: string, email: string) =>
    unwrap<{ sentTo: string; expiresInMinutes: number }>(apiClient.post(`/refer/v/${token}/code`, { email })),
  publicVerifyCode: (token: string, email: string, code: string) =>
    unwrap<{ viewerToken: string; email: string; firstTime: boolean }>(apiClient.post(`/refer/v/${token}/verify`, { email, code })),
  publicView: (token: string) => unwrap<PublicReferralView>(apiClient.get(`/refer/v/${token}`, viewer())),
  publicStatus: (token: string, input: StatusChangeInput) =>
    unwrap<PublicReferralView>(apiClient.post(`/refer/v/${token}/status`, input, viewer())),
  publicNote: (token: string, note: string, actorName?: string) =>
    unwrap<PublicReferralView>(apiClient.post(`/refer/v/${token}/notes`, { note, actorName }, viewer())),
  publicReport: (token: string, input: ReportInput) =>
    unwrap<PublicReferralView>(apiClient.post(`/refer/v/${token}/reports`, input, viewer())),
  publicRequestUpload: (token: string, input: AttachmentUploadInput) =>
    unwrap<AttachmentUploadTicket>(apiClient.post(`/refer/v/${token}/attachments/upload-url`, input, viewer())),
  publicConfirmUpload: (token: string, attachmentId: string) =>
    unwrap<ReferralAttachment>(apiClient.post(`/refer/v/${token}/attachments/${attachmentId}/confirm`, {}, viewer())),
  publicClaim: (token: string, input: { contactName?: string | null; practiceName?: string | null; phone?: string | null }) =>
    unwrap<{ saved: true }>(apiClient.post(`/refer/v/${token}/claim`, input, viewer())),
  patientView: (token: string) => unwrap<PatientReferralView>(apiClient.get(`/refer/p/${token}`)),

  // Agency: outside offices that verified a contact on a referral link.
  agencyClaimedOffices: (practiceId?: string) =>
    unwrap<ClaimedOffice[]>(apiClient.get('/agency/referral-offices', { params: practiceId ? { practiceId } : {} })),
};

/** PUT the bytes straight to object storage, then tell the API the file landed. */
export async function uploadToSignedUrl(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

/** The MIME type we send for a file; DICOM and odd X-ray exports often arrive untyped. */
export function attachmentContentType(file: File): AttachmentUploadInput['contentType'] {
  const allowed = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/tiff',
    'application/pdf',
    'application/dicom',
  ];
  if (allowed.includes(file.type)) return file.type;
  if (/\.(dcm|dicom)$/i.test(file.name)) return 'application/dicom';
  return 'application/octet-stream';
}

export function apiErrorCode(err: unknown): string | null {
  return (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code ?? null;
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
    ?.error?.message;
  return message ?? (err instanceof Error ? err.message : fallback);
}
